import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Checkbox } from '../ui/checkbox';
import { toast } from 'sonner';
import api from '../../services/api';
import { 
  Phone, 
  AlertTriangle, 
  Calendar,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw,
  FileText,
  Shield
} from 'lucide-react';

const CallOutTab = ({ schedules = [] }) => {
  const [callouts, setCallouts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  
  // Form state
  const [selectedBooking, setSelectedBooking] = useState('');
  const [calloutType, setCalloutType] = useState('');
  const [reason, setReason] = useState('');
  const [contactedSupervisor, setContactedSupervisor] = useState(false);
  const [contactMethod, setContactMethod] = useState('');

  const fetchData = async () => {
    try {
      setLoading(true);
      const [calloutsRes, statsRes] = await Promise.all([
        api.get('/callouts'),
        api.get('/callouts/my-stats')
      ]);
      setCallouts(calloutsRes.data.callouts || []);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Failed to fetch call out data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedBooking || !calloutType || !reason.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    const booking = schedules.find(s => s.booking_id === selectedBooking || s.id === selectedBooking);
    if (!booking) {
      toast.error('Please select a valid shift');
      return;
    }

    // Check if short notice
    const shiftDate = new Date(booking.date || booking.scheduled_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const daysNotice = Math.floor((shiftDate - today) / (1000 * 60 * 60 * 24));
    
    if (daysNotice < 1 && !contactedSupervisor) {
      toast.error('For call outs with less than 24 hours notice, you must contact the supervisor first');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/callouts', {
        booking_id: booking.booking_id || booking.id,
        shift_date: booking.date || booking.scheduled_date,
        shift_time: booking.start_time || booking.scheduled_time || '',
        callout_type: calloutType,
        reason: reason,
        contacted_supervisor: contactedSupervisor,
        supervisor_contact_method: contactMethod || null
      });
      
      toast.success('Call out submitted successfully');
      setShowForm(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit call out');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setSelectedBooking('');
    setCalloutType('');
    setReason('');
    setContactedSupervisor(false);
    setContactMethod('');
  };

  const getDisciplineBadge = (level) => {
    const badges = {
      none: { color: 'bg-[var(--success)]/15 text-[var(--success)]', label: 'Good Standing' },
      warning: { color: 'bg-[var(--warning)]/15 text-[var(--warning)]', label: 'Warning (3)' },
      second_warning: { color: 'bg-orange-100 text-orange-800', label: '2nd Warning (5)' },
      probation: { color: 'bg-[var(--error)]/15 text-[var(--error)]', label: 'Probation (8)' },
      deactivated: { color: 'bg-gray-800 text-white', label: 'Deactivated (9)' }
    };
    const badge = badges[level] || badges.none;
    return <Badge className={badge.color}>{badge.label}</Badge>;
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { color: 'bg-[#D4AF37]/10 text-blue-800', icon: Clock },
      approved: { color: 'bg-[var(--success)]/15 text-[var(--success)]', icon: CheckCircle },
      rejected: { color: 'bg-[var(--error)]/15 text-[var(--error)]', icon: XCircle }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <Badge className={`${badge.color} flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  const upcomingShifts = schedules.filter(s => {
    const shiftDate = new Date(s.date || s.scheduled_date);
    return shiftDate >= new Date();
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-pink-500" />
      </div>
    );
  }

  // Check if worker is deactivated
  if (stats?.discipline_level === 'deactivated') {
    return (
      <div className="space-y-6" data-testid="callout-tab">
        <Card className="bg-[var(--error)]/10 border-[var(--error)]/30">
          <CardContent className="p-8 text-center">
            <Shield className="h-16 w-16 mx-auto text-[var(--error)] mb-4" />
            <h2 className="text-xl font-bold text-[var(--error)] mb-2">Account Deactivated</h2>
            <p className="text-[var(--error)] mb-4">
              Your account has been deactivated due to reaching 9 call outs this year.
              Please contact your supervisor to discuss reactivation.
            </p>
            <p className="text-sm text-[var(--error)]">
              Call outs reset at the beginning of each year.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="callout-tab">
      {/* Stats Overview */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-cyan-50 border-blue-100">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-[#D4AF37]">{stats?.approved_count || 0}</p>
            <p className="text-sm text-[#D4AF37]/70">Call Outs This Year</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-100">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-[var(--warning)]">{stats?.pending_count || 0}</p>
            <p className="text-sm text-[var(--warning)]/70">Pending</p>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-100">
          <CardContent className="p-4 text-center">
            <p className="text-3xl font-bold text-[var(--success)]">{stats?.callouts_until_warning || 0}</p>
            <p className="text-sm text-[var(--success)]/70">Until Warning</p>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-sm text-[var(--text-muted)] mb-1">Status</p>
            {getDisciplineBadge(stats?.discipline_level)}
          </CardContent>
        </Card>
      </div>

      {/* Warning Banner */}
      {stats?.discipline_level && stats.discipline_level !== 'none' && (
        <Card className={`${
          stats.discipline_level === 'probation' ? 'bg-[var(--error)]/10 border-[var(--error)]/30' :
          stats.discipline_level === 'second_warning' ? 'bg-orange-50 border-orange-200' :
          'bg-[var(--warning)]/10 border-[var(--warning)]/30'
        }`}>
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className={`h-6 w-6 ${
              stats.discipline_level === 'probation' ? 'text-[var(--error)]' :
              stats.discipline_level === 'second_warning' ? 'text-orange-500' :
              'text-yellow-500'
            }`} />
            <div>
              <p className="font-medium">{stats.discipline_message}</p>
              {stats.discipline_level === 'probation' && (
                <p className="text-sm text-[var(--error)]">One more call out will result in account deactivation.</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Submit Call Out Button */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold">Call Outs</h2>
        <Button 
          onClick={() => setShowForm(!showForm)}
          className="bg-pink-500 hover:bg-pink-600"
          data-testid="submit-callout-btn"
        >
          {showForm ? 'Cancel' : 'Submit Call Out'}
        </Button>
      </div>

      {/* Call Out Form */}
      {showForm && (
        <Card className="border-pink-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Phone className="h-5 w-5 text-pink-500" />
              Submit Call Out Request
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Select Shift */}
              <div>
                <Label className="font-semibold">Select Shift *</Label>
                <Select value={selectedBooking} onValueChange={setSelectedBooking}>
                  <SelectTrigger data-testid="shift-select">
                    <SelectValue placeholder="Select the shift you're calling out for" />
                  </SelectTrigger>
                  <SelectContent>
                    {upcomingShifts.length === 0 ? (
                      <SelectItem value="none" disabled>No upcoming shifts</SelectItem>
                    ) : (
                      upcomingShifts.map(shift => (
                        <SelectItem 
                          key={shift.booking_id || shift.id} 
                          value={shift.booking_id || shift.id}
                        >
                          {shift.date || shift.scheduled_date} - {shift.service_name || 'Service'} ({shift.customer_name})
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>

              {/* Call Out Type */}
              <div>
                <Label className="font-semibold">Reason Type *</Label>
                <Select value={calloutType} onValueChange={setCalloutType}>
                  <SelectTrigger data-testid="type-select">
                    <SelectValue placeholder="Select reason type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sick">Sick</SelectItem>
                    <SelectItem value="emergency">Emergency</SelectItem>
                    <SelectItem value="personal">Personal</SelectItem>
                    <SelectItem value="family">Family Matter</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Reason */}
              <div>
                <Label className="font-semibold">Reason Details *</Label>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="Please provide details about your call out..."
                  rows={3}
                  data-testid="reason-input"
                />
              </div>

              {/* Supervisor Contact */}
              <Card className="bg-[var(--warning)]/10 border-[var(--warning)]/30">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <Checkbox
                      id="contacted"
                      checked={contactedSupervisor}
                      onCheckedChange={setContactedSupervisor}
                      data-testid="contacted-checkbox"
                    />
                    <div>
                      <Label htmlFor="contacted" className="font-medium cursor-pointer">
                        I have contacted the supervisor
                      </Label>
                      <p className="text-sm text-amber-700 mt-1">
                        <strong>Required for short notice call outs (less than 24 hours).</strong> 
                        You must contact the supervisor before submitting.
                      </p>
                    </div>
                  </div>
                  
                  {contactedSupervisor && (
                    <div className="mt-3 ml-6">
                      <Label className="text-sm">How did you contact them?</Label>
                      <Select value={contactMethod} onValueChange={setContactMethod}>
                        <SelectTrigger className="mt-1">
                          <SelectValue placeholder="Select contact method" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="phone">Phone Call</SelectItem>
                          <SelectItem value="text">Text Message</SelectItem>
                          <SelectItem value="email">Email</SelectItem>
                          <SelectItem value="inperson">In Person</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="flex gap-3 pt-2">
                <Button 
                  type="submit" 
                  disabled={submitting}
                  className="bg-pink-500 hover:bg-pink-600"
                  data-testid="submit-form-btn"
                >
                  {submitting ? 'Submitting...' : 'Submit Call Out'}
                </Button>
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => { setShowForm(false); resetForm(); }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Call Out Policy Notice */}
      <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-100">
        <CardContent className="p-4">
          <h4 className="font-medium text-blue-900 mb-2 flex items-center gap-2">
            <FileText className="h-4 w-4" />
            Call Out Policy
          </h4>
          <ul className="text-sm text-blue-800 space-y-1">
            <li>• Call outs must be submitted at least 24 hours before your shift</li>
            <li>• Short notice call outs require supervisor contact</li>
            <li>• <strong>3 call outs</strong> = Warning</li>
            <li>• <strong>5 call outs</strong> = 2nd Warning</li>
            <li>• <strong>8 call outs</strong> = Probation</li>
            <li>• <strong>9 call outs</strong> = Account Deactivation</li>
            <li>• Call out counts reset at the beginning of each year</li>
          </ul>
        </CardContent>
      </Card>

      {/* Call Out History */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Call Out History</CardTitle>
        </CardHeader>
        <CardContent>
          {callouts.length === 0 ? (
            <div className="text-center py-8 text-[var(--text-muted)]">
              <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No call outs recorded</p>
            </div>
          ) : (
            <div className="space-y-3">
              {callouts.map(callout => (
                <div 
                  key={callout.id}
                  className="flex items-center justify-between p-4 bg-[var(--background-subtle)] rounded-lg"
                  data-testid={`callout-${callout.id}`}
                >
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium">{callout.shift_date}</span>
                      {getStatusBadge(callout.status)}
                      {callout.is_short_notice && (
                        <Badge variant="outline" className="text-amber-600 border-amber-300">
                          Short Notice
                        </Badge>
                      )}
                    </div>
                    <p className="text-sm text-[var(--text-secondary)]">
                      {callout.service_name} - {callout.callout_type}
                    </p>
                    <p className="text-sm text-[var(--text-muted)] mt-1">{callout.reason}</p>
                    {/* Owner Notes - show for all statuses if present */}
                    {callout.owner_notes && (
                      <div className={`mt-2 p-2 rounded text-sm ${
                        callout.status === 'rejected' ? 'bg-[var(--error)]/10 text-red-700 border border-[var(--error)]/30' :
                        callout.status === 'approved' ? 'bg-[var(--success)]/10 text-green-700 border border-[var(--success)]/30' :
                        'bg-[#D4AF37]/10 text-[#D4AF37] border border-[#D4AF37]/30'
                      }`}>
                        <strong>Supervisor Note:</strong> {callout.owner_notes}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CallOutTab;
